import { computed, ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  LISTING_STATUS_KPI_ALL,
  LISTING_STATUS_KPIS,
  type ListingStatusKpi,
} from '../constants/listingRequestLifecycle'

// `?filter=inProgress` → that card. Unknown or repeated values fall back to
// "All requests" rather than filtering on something the page never offered.
function kpiFromQuery(raw: unknown): ListingStatusKpi {
  return LISTING_STATUS_KPIS.find((kpi) => kpi.key === raw) ?? LISTING_STATUS_KPI_ALL
}

/**
 * The Sell Request table's status filter, picked through the KPI cards. The
 * card's key — not its status list, which runs to nine values for "In progress"
 * — is mirrored into the URL as `?filter=`, so returning from a request's detail
 * page or refreshing lands the seller back on the card they picked. Every change
 * resets `page` to 1: a new filter is a new list.
 */
export function useListingStatusFilter(page: Ref<number>) {
  const route = useRoute()
  const router = useRouter()

  const selected = ref<ListingStatusKpi>(kpiFromQuery(route.query.filter))
  const selectedKey = computed(() => selected.value.key)
  const statuses = computed(() => selected.value.statuses)
  const label = computed(() => selected.value.label)
  const isFiltered = computed(() => selected.value.statuses.length > 0)

  // Takes any key, so a filter card can hand over its own; an unknown key is All.
  function select(key: string) {
    selected.value = LISTING_STATUS_KPIS.find((kpi) => kpi.key === key) ?? LISTING_STATUS_KPI_ALL
  }

  function clear() {
    selected.value = LISTING_STATUS_KPI_ALL
  }

  watch(selected, (next) => {
    page.value = 1
    const query = { ...route.query }
    delete query.filter
    if (next.statuses.length) query.filter = next.key
    void router.replace({ query })
  })

  return { selectedKey, statuses, label, isFiltered, select, clear }
}
